import { sortBy } from 'lodash';
import { OUTCOME_MEASUREMENT_TYPES } from 'src/constants/prompt-level-template';

export const isNumericOutcome = (type) => {
  return [
    OUTCOME_MEASUREMENT_TYPES.WEIGHTED,
    OUTCOME_MEASUREMENT_TYPES.RATING_SCALE,
  ].includes(type);
};

export const isRatingScale = (type) => {
  return type === OUTCOME_MEASUREMENT_TYPES.RATING_SCALE;
};

/**
 * Formats the raw rating score value to a consistent string representation.
 * @param {number|null|undefined} score
 * @returns {string}
 */
export const formatRatingScore = (score) => {
  return score === null || score === undefined ? '-' : `${Number(score).toFixed(1)}`;
};

/**
 * @param {Array<{ id: string }>} activePromptLevels Active prompt level templates.
 * @param {string} templateId
 * @returns {string|null} The id when that template is still active, otherwise null.
 */
export const getActivePromptLevelTemplateId = (activePromptLevels, templateId) => (
  activePromptLevels.find(({ id }) => id === templateId)?.id ?? null
);

/**
 * @param {Object} program
 * @param {string} promptLevelTemplateId
 * @returns {Object|null} The program's customization of that template, if it has one.
 */
export const getCustomizedProgramPromptLevels = (program, promptLevelTemplateId) => {
  const customized = program?.customizedPromptLevels ?? null;
  if (!customized || customized.promptLevelId !== promptLevelTemplateId) {
    return null;
  }
  return customized;
};

/**
 * Hides and reorders a template's prompt levels as a program has customized them.
 *
 * @param {Array<Object>} promptLevels
 * @param {Object|null} customizedProgramPromptLevels
 * @returns {Array<Object>}
 */
export const applyCustomizedPromptLevels = (promptLevels, customizedProgramPromptLevels) => {
  if (!customizedProgramPromptLevels) {
    return promptLevels;
  }

  const { visiblePromptLevels = [], hiddenPromptLevels = [] } = customizedProgramPromptLevels;

  const hiddenIds = new Set(hiddenPromptLevels.map(({ id }) => id));

  const visibleOrderById = visiblePromptLevels.reduce((acc, { id, order }) => {
    acc[id] = order;
    return acc;
  }, {});

  const maxVisibleOrder = visiblePromptLevels.reduce(
    (max, { order }) => Math.max(max, order || 0),
    0,
  );

  let nextOrder = maxVisibleOrder + 1;

  const modifiedPrompts = promptLevels.reduce((acc, pl) => {
    if (hiddenIds.has(pl.id)) {
      return acc;
    }

    // eslint-disable-next-line no-plusplus
    const order = visibleOrderById[pl.id] ?? nextOrder++;

    acc.push({ ...pl, order });
    return acc;
  }, []);

  return sortBy(modifiedPrompts, 'order');
};
